import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, User, Lock, Eye, EyeOff, AlertTriangle, ArrowLeft } from 'lucide-react';
import { trackAuth, trackError } from '../Utils/analytics';
import '../Admin/AdminLogin.css';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      trackError('admin_login_validation_failed', 'admin_login');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Since we don't have a real admin API, let's create a simple mock authentication
      // In production, replace this with your actual admin API endpoint
      
      // Mock admin credentials for testing
      const mockAdminEmail = 'admin@homecookt.com';
      const mockAdminPassword = 'admin123!';
      
      if (email === mockAdminEmail && password === mockAdminPassword) {
        // Mock successful login
        const mockToken = 'mock-admin-token-' + Date.now();
        
        localStorage.setItem('adminToken', mockToken);
        localStorage.setItem('adminEmail', email);
        localStorage.setItem('adminRole', 'admin');
        
        trackAuth('login');
        navigate('/admin/dashboard');
        return;
      }
      
      // Try real API if mock fails
      const response = await fetch('https://api.homecookt.com/api/v1/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Handle the admin access token
        const token = data.access_token || data.token || data.authToken || data.adminToken;
        
        if (token) {
          localStorage.setItem('adminToken', token);
          localStorage.setItem('adminEmail', email);
          localStorage.setItem('adminRole', data.role || 'admin');
          
          // Store admin permissions if provided
          if (data.permissions) {
            localStorage.setItem('adminPermissions', JSON.stringify(data.permissions));
          }
          
          trackAuth('login');
          
          // Redirect to admin dashboard
          navigate('/admin/dashboard');
        } else {
          setError('Login successful but authentication token not found. Please contact support.');
          trackError('admin_login_token_missing', 'admin_login');
        }
      } else {
        // Handle different error scenarios
        if (response.status === 401) {
          setError('Invalid admin credentials. Use admin@homecookt.com / admin123! for testing.');
          trackError('admin_login_invalid_credentials', 'admin_login');
        } else if (response.status === 403) {
          setError('Access denied. You do not have admin privileges.');
          trackError('admin_login_access_denied', 'admin_login');
        } else if (response.status === 404) {
          setError('Admin API not available. Use admin@homecookt.com / admin123! for testing.');
          trackError('admin_login_endpoint_not_found', 'admin_login');
        } else if (response.status === 429) {
          setError('Too many login attempts. Please wait before trying again.');
          trackError('admin_login_rate_limited', 'admin_login');
        } else if (response.status >= 500) {
          setError('Server error. Please try again later.');
          trackError('admin_login_server_error', 'admin_login');
        } else {
          setError('API unavailable. Use admin@homecookt.com / admin123! for testing.');
          trackError('admin_login_unknown_error', 'admin_login');
        }
      }
    } catch (error) {
      console.error('Admin login error:', error);
      setError('API unavailable. Use admin@homecookt.com / admin123! for testing.');
      trackError('admin_login_network_error', 'admin_login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="homecookt-admin-login-page">
      <div className="homecookt-admin-login-container">
        {/* Header */}
        <div className="homecookt-admin-login-header">
          <div className="homecookt-admin-login-icon">
            <Shield size={40} />
          </div>
          <h1 className="homecookt-admin-login-title">Admin Portal</h1>
          <p className="homecookt-admin-login-subtitle">Secure access for administrators</p>
        </div>

        {/* Login Form */}
        <form className="homecookt-admin-login-form" onSubmit={handleLogin}>
          <div className="homecookt-admin-form-group">
            <label htmlFor="admin-email">Admin Email</label>
            <div className="homecookt-admin-input-wrapper">
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={error ? 'homecookt-admin-error' : ''}
                placeholder="Enter your admin email"
                required
                autoComplete="username"
              />
              <User className="homecookt-admin-input-icon" size={20} />
            </div>
          </div>

          <div className="homecookt-admin-form-group">
            <label htmlFor="admin-password">Password</label>
            <div className="homecookt-admin-input-wrapper">
              <div className="homecookt-admin-password-wrapper">
                <input
                  id="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={error ? 'homecookt-admin-error' : ''}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <Lock className="homecookt-admin-input-icon" size={20} />
                <button
                  type="button"
                  className="homecookt-admin-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="homecookt-admin-error-message">
              <AlertTriangle className="homecookt-admin-error-icon" size={20} />
              <span>{error}</span>
            </div>
          )}

          <button 
            type="submit" 
            className="homecookt-admin-login-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="homecookt-admin-login-spinner"></div>
                <span>Authenticating...</span>
              </>
            ) : (
              <>
                <Shield size={20} />
                <span>Access Admin Portal</span>
              </>
            )}
          </button>
        </form>

        {/* Security Notice */}
        <div className="homecookt-admin-security-notice">
          <Shield className="homecookt-admin-security-icon" size={20} />
          <div className="homecookt-admin-security-text">
            This is a secure admin area. All login attempts are monitored and logged.
          </div>
        </div>

        {/* Back to Home */}
        <Link to="/" className="homecookt-admin-back-link">
          <ArrowLeft size={16} />
          <span>Back to Homepage</span>
        </Link>
      </div>
    </div>
  );
};

export default AdminLogin;